import React, { lazy, Suspense, useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import './index.css';
import Auth from './Auth';
import reportWebVitals from './reportWebVitals';
import { preloadRoute, prefetchDashboardData } from './dashboardPrefetch';

const App = lazy(() => import('./App'));
const Categorizer = lazy(() => import('./Categorizer'));
const TransactionEdit = lazy(() => import('./TransactionEdit'));
const AllTransactions = lazy(() => import('./AllTransactions'));
const CashFlow = process.env.REACT_APP_CASHFLOW_V2_ENABLED === 'true'
  ? lazy(() => import('./CashFlowV2'))
  : lazy(() => import('./CashFlow'));
const NetWorth = lazy(() => import('./NetWorth'));

const NAV_LINKS = [
  { to: '/', label: 'Dashboard', match: p => p === '/' },
  { to: '/categorizer', label: 'Categorizer', match: p => p === '/categorizer' },
  { to: '/all-transactions', label: 'All Transactions', match: p => p === '/all-transactions' },
  { to: '/cashflow', label: 'Cash Flow', match: p => p === '/cashflow' },
  { to: '/networth', label: 'Net Worth', match: p => p === '/networth' },
];

function Navigation() {
  const location = useLocation();
  const isTransaction = location.pathname.startsWith('/transaction/');
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close menu on navigation
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const linkStyle = (active) => ({
    padding: '10px 25px',
    fontSize: '16px',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '6px',
    textDecoration: 'none',
    backgroundColor: active ? '#fff' : 'transparent',
    color: active ? '#1a237e' : '#fff',
    transition: 'all 0.2s',
    display: 'block',
    ...(isMobile ? { width: '100%', textAlign: 'center', padding: '12px 20px' } : {}),
  });

  return (
    <nav style={{
      backgroundColor: '#1a237e',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      position: 'relative',
    }}>
      {isMobile ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px' }}>
            <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '16px' }}>
              {NAV_LINKS.find(l => l.match(location.pathname))?.label || 'Expenses'}
            </span>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '8px',
                display: 'flex', flexDirection: 'column', gap: '4px',
              }}
              aria-label="Toggle menu"
            >
              <span style={{ display: 'block', width: '22px', height: '2px', backgroundColor: '#fff', borderRadius: '1px', transition: 'all 0.2s', transform: menuOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none' }} />
              <span style={{ display: 'block', width: '22px', height: '2px', backgroundColor: '#fff', borderRadius: '1px', transition: 'all 0.2s', opacity: menuOpen ? 0 : 1 }} />
              <span style={{ display: 'block', width: '22px', height: '2px', backgroundColor: '#fff', borderRadius: '1px', transition: 'all 0.2s', transform: menuOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none' }} />
            </button>
          </div>
          {menuOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '0 10px 10px' }}>
              {NAV_LINKS.map(link => (
                <Link key={link.to} to={link.to} style={linkStyle(link.match(location.pathname))} onMouseEnter={() => preloadRoute(link.to)} onFocus={() => preloadRoute(link.to)}>
                  {link.label}
                </Link>
              ))}
              {isTransaction && (
                <span style={linkStyle(true)}>Edit Transaction</span>
              )}
            </div>
          )}
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', padding: '15px' }}>
          {NAV_LINKS.map(link => (
            <Link key={link.to} to={link.to} style={linkStyle(link.match(location.pathname))} onMouseEnter={() => preloadRoute(link.to)} onFocus={() => preloadRoute(link.to)}>
              {link.label}
            </Link>
          ))}
          {isTransaction && (
            <span style={linkStyle(true)}>Edit Transaction</span>
          )}
        </div>
      )}
    </nav>
  );
}

function Root() {
  useEffect(() => {
    if (navigator.connection?.saveData) return undefined;
    const run = () => prefetchDashboardData();
    const idleId = window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 1200 }) : window.setTimeout(run, 400);
    return () => window.requestIdleCallback ? window.cancelIdleCallback(idleId) : window.clearTimeout(idleId);
  }, []);
  return (
    <BrowserRouter>
      <div>
        <Navigation />
        <Suspense fallback={<main className="route-loading" aria-live="polite">Opening saved view…</main>}>
          <Routes>
            <Route path="/" element={<App />} />
            <Route path="/categorizer" element={<Categorizer />} />
            <Route path="/all-transactions" element={<AllTransactions />} />
            <Route path="/cashflow" element={<CashFlow />} />
            <Route path="/networth" element={<NetWorth />} />
            <Route path="/transaction/:id" element={<TransactionEdit />} />
          </Routes>
        </Suspense>
      </div>
    </BrowserRouter>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Auth>
      <Root />
    </Auth>
  </React.StrictMode>
);

reportWebVitals();
